import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Camera, Server, HardDrive, Monitor, Wifi, Shield,
  Activity, Cpu, Router, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, Globe, Network, Fingerprint, Radio, Eye, Info,
  Clock, ChevronDown, ChevronUp, ExternalLink, Gauge, Layers, Terminal, Radar, Loader2
} from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';
import CameraAnalyticsSection from '../components/CameraAnalyticsSection';
import '../styles/v2/pages/md-discovery-detail.css';

const DEVICE_ICONS = {
  CAMERA: Camera, SERVER: Server, ROUTER: Router, SWITCH: HardDrive,
  NVR: Monitor, WORKSTATION: Monitor, ACCESS_POINT: Wifi, BMC: Cpu,
  DOOR_CONTROLLER: Shield, INTERCOM: Activity, PRINTER: Monitor, UNKNOWN: Cpu
};

const STATUS_CONFIG = {
  ONLINE: { color: '#00c853', label: 'Online', Icon: CheckCircle },
  OFFLINE: { color: '#ff1744', label: 'Offline', Icon: XCircle },
  UNKNOWN: { color: '#78909c', label: 'Unknown', Icon: AlertTriangle }
};

function ConfidenceBadge({ score }) {
  let color, label;
  if (score >= 80) { color = '#00c853'; label = 'High'; }
  else if (score >= 40) { color = '#ffaa00'; label = 'Medium'; }
  else if (score > 0) { color = '#ff6d00'; label = 'Low'; }
  else { color = '#78909c'; label = 'None'; }
  return (
    <div className="dd-confidence-badge" style={{ '--conf-color': color }}>
      <Gauge size={16} />
      <div className="dd-confidence-bar-track">
        <div className="dd-confidence-bar-fill" style={{ width: score + '%', background: color }} />
      </div>
      <span className="dd-confidence-score">{score}%</span>
      <span className="dd-confidence-label">{label}</span>
    </div>
  );
}

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = true, count }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={'dd-section' + (open ? ' dd-section-open' : '')}>
      <button className="dd-section-header" onClick={() => setOpen(!open)}>
        <div className="dd-section-title">
          {Icon && <Icon size={16} />}
          <span>{title}</span>
          {count !== undefined && <span className="dd-section-count">{count}</span>}
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="dd-section-body">{children}</div>}
    </div>
  );
}

function InfoRow({ label, value, mono, link }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="dd-info-row">
      <span className="dd-info-label">{label}</span>
      {link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="dd-info-value dd-info-link">
          {value} <ExternalLink size={12} />
        </a>
      ) : (
        <span className={'dd-info-value' + (mono ? ' dd-mono' : '')}>{String(value)}</span>
      )}
    </div>
  );
}

// ONVIF probe-target promotion ([9.2]). Lets an operator opt a discovered
// device into ONVIF analytics probing with an explicit credential override.
// The toggle is disabled until detection has flagged the device onvifCapable.
function OnvifPromotion({ device, onUpdated }) {
  const [enabled, setEnabled] = useState(!!device.onvifEnabled);
  const [port, setPort] = useState(device.onvifPort != null ? String(device.onvifPort) : '');
  const [credentialId, setCredentialId] = useState(device.onvifCredentialId || '');
  const [creds, setCreds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'error'|'success', text }

  const capable = !!device.onvifCapable;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithTenant('/api/md/credentials');
        const data = await res.json();
        if (cancelled) return;
        const onvif = (data.data || []).filter(c => c.protocol === 'ONVIF');
        setCreds(onvif);
      } catch (err) {
        console.error('Failed to load ONVIF credentials:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const body = {
        enabled,
        port: port.trim() === '' ? null : Number(port),
        credentialId: credentialId || null,
      };
      const res = await fetchWithTenant('/api/md/devices/' + device.id + '/onvif', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setMsg({ type: 'error', text: data.error || 'Failed to save ONVIF settings' });
      } else {
        setMsg({ type: 'success', text: 'ONVIF settings saved' });
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to save ONVIF settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dd-sub-section">
      <div className="dd-sub-title">ONVIF Probing</div>
      <label
        className="dd-info-row"
        style={{ cursor: 'pointer' }}
        title={capable ? '' : 'Not auto-detected as ONVIF-capable. You can still enable probing to attempt a probe with explicit settings.'}
      >
        <span className="dd-info-label">Enable ONVIF probing</span>
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => setEnabled(e.target.checked)}
        />
      </label>

      <div className="dd-info-row">
        <span className="dd-info-label">Port</span>
        <input
          type="number"
          min="1"
          max="65535"
          placeholder="80"
          value={port}
          onChange={e => setPort(e.target.value)}
          className="dd-input"
          style={{ maxWidth: 100 }}
        />
      </div>

      <div className="dd-info-row">
        <span className="dd-info-label">Credential</span>
        <select
          value={credentialId}
          onChange={e => setCredentialId(e.target.value)}
          className="dd-input"
        >
          <option value="">Use tenant default</option>
          {creds.map(c => (
            <option key={c.id} value={c.id}>
              {c.label}{c.isDefault ? ' (default)' : ''}
            </option>
          ))}
        </select>
      </div>

      {msg && (
        <div className={'dd-info-row ' + (msg.type === 'error' ? 'dd-text-red' : 'dd-text-green')}>
          {msg.text}
        </div>
      )}

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
        style={{ marginTop: 8 }}
      >
        {saving ? 'Saving…' : 'Save ONVIF settings'}
      </button>
    </div>
  );
}

export default function MdDiscoveryDeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [rescanMsg, setRescanMsg] = useState(null); // { type: 'error'|'success', text }
  const rescanTimers = useRef({ poll: null, timeout: null, clear: null });

  const clearRescanTimers = useCallback(() => {
    const t = rescanTimers.current;
    if (t.poll) { clearInterval(t.poll); t.poll = null; }
    if (t.timeout) { clearTimeout(t.timeout); t.timeout = null; }
    if (t.clear) { clearTimeout(t.clear); t.clear = null; }
  }, []);

  useEffect(() => clearRescanTimers, [clearRescanTimers]); // clear timers on unmount

  const fetchDevice = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/devices/' + id);
      const data = await res.json();
      if (data.device) setDevice(data.device);
    } catch (err) {
      console.error('Failed to fetch device:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDevice(); }, [fetchDevice]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDevice();
    setRefreshing(false);
  };

  const handleRescan = async () => {
    if (rescanning) return; // guard against rapid double-click
    clearRescanTimers();
    setRescanning(true);
    setRescanMsg({ type: 'success', text: 'Sending re-scan command...' });

    const finish = (msg, ms) => {
      clearRescanTimers();
      setRescanMsg(msg);
      rescanTimers.current.clear = setTimeout(() => {
        setRescanning(false);
        setRescanMsg(null);
      }, ms);
    };

    try {
      const res = await fetchWithTenant('/api/md/devices/' + id + '/rescan', { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || ('HTTP ' + res.status));
      }

      const snapshotUpdatedAt = device?.updatedAt;
      setRescanMsg({ type: 'success', text: 'Re-scan in progress...' });

      rescanTimers.current.poll = setInterval(async () => {
        try {
          const r = await fetchWithTenant('/api/md/devices/' + id);
          if (!r.ok) return;
          const d = await r.json();
          const next = d?.device?.updatedAt;
          if (next && next !== snapshotUpdatedAt) {
            if (d.device) setDevice(d.device);
            finish({ type: 'success', text: 'Re-scan complete - refreshed device data' }, 5000);
          }
        } catch (_) { /* ignore individual poll errors */ }
      }, 2000);

      rescanTimers.current.timeout = setTimeout(() => {
        finish({ type: 'success', text: 'Re-scan completed (no changes detected within 30s)' }, 5000);
      }, 30000);
    } catch (err) {
      finish({ type: 'error', text: 'Error: ' + (err.message || 'unknown') }, 10000);
    }
  };

  if (loading) {
    return (
      <div className="dd-loading">
        <RefreshCw size={24} className="spin" />
        <span>Loading device details...</span>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="dd-not-found">
        <AlertTriangle size={48} />
        <h2>Device Not Found</h2>
        <button className="btn btn-primary" onClick={() => navigate('/monitoring/discovery')}>Back to Discovery</button>
      </div>
    );
  }

  const DevIcon = DEVICE_ICONS[device.deviceType] || Cpu;
  const statusCfg = STATUS_CONFIG[device.status] || STATUS_CONFIG.UNKNOWN;
  const StatusIcon = statusCfg.Icon;
  const fp = device.httpFingerprint;
  const snmp = device.snmpData;
  const bmc = device.bmcData;
  const rtsp = device.rtspStreams;

  return (
    <div className="dd-container">
      <div className="dd-header">
        <button className="dd-back-btn" onClick={() => navigate('/monitoring/discovery')}>
          <ArrowLeft size={18} /> Discovery
        </button>
        {device.agentId && device.ipAddress && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate(
              `/monitoring/manage/${device.agentId}/${device.ipAddress}/80?name=${encodeURIComponent(device.name || device.hostname || device.ipAddress)}&return=/monitoring/discovery/${device.id}`
            )}
            style={{ marginLeft: 'auto', marginRight: 8 }}
          >
            <ExternalLink size={14} /> Manage Device
          </button>
        )}
        {rescanMsg && (
          <span
            className={rescanMsg.type === 'error' ? 'dd-text-red' : 'dd-text-green'}
            style={{ marginRight: 8, fontSize: 13 }}
          >
            {rescanMsg.text}
          </span>
        )}
        <button
          className={'dd-refresh-btn' + (rescanning ? ' spinning' : '')}
          onClick={handleRescan}
          disabled={rescanning}
          title="Re-scan this device"
        >
          {rescanning ? <Loader2 size={16} className="spin" /> : <Radar size={16} />}
        </button>
        <button className={'dd-refresh-btn' + (refreshing ? ' spinning' : '')} onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="dd-identity-card">
        <div className="dd-identity-top">
          <div className="dd-device-icon" style={{ background: statusCfg.color + '20', color: statusCfg.color }}>
            <DevIcon size={32} />
          </div>
          <div className="dd-identity-info">
            <h1 className="dd-device-name">{device.name || device.hostname || device.ipAddress}</h1>
            <div className="dd-identity-meta">
              <span className="dd-status-pill" style={{ background: statusCfg.color + '20', color: statusCfg.color }}>
                <StatusIcon size={12} /> {statusCfg.label}
              </span>
              <span className="dd-type-pill"><DevIcon size={12} /> {device.deviceType}</span>
              {device.manufacturer && <span className="dd-mfg-pill">{device.manufacturer}</span>}
              {device.model && <span className="dd-model-pill">{device.model}</span>}
            </div>
          </div>
        </div>
        {device.confidence > 0 && (
          <div className="dd-confidence-section">
            <ConfidenceBadge score={device.confidence} />
            {device.classificationReasons?.length > 0 && (
              <div className="dd-confidence-reasons">
                {device.classificationReasons.map((r, i) => (
                  <span key={i} className="dd-reason-tag"><Fingerprint size={11} /> {r}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="dd-grid">
        <CollapsibleSection title="Network Identity" icon={Network}>
          <InfoRow label="IP Address" value={device.ipAddress} mono />
          <InfoRow label="MAC Address" value={device.macAddress} mono />
          <InfoRow label="Hostname" value={device.hostname} mono />
          <InfoRow label="Web Interface" value={device.webInterface} link />
          <InfoRow label="Agent" value={device.agent?.name || device.agent?.hostname} />
          <InfoRow label="Source" value={device.source} />
        </CollapsibleSection>

        {device.isWireless && (
          <CollapsibleSection title="Wireless" icon={Wifi}>
            <InfoRow label="Access Point" value={device.wirelessApName || device.wirelessApMac} mono={!device.wirelessApName} />
            <InfoRow label="SSID" value={device.wirelessSsid} />
            <InfoRow label="Signal" value={device.wirelessSignalDbm != null ? `${device.wirelessSignalDbm} dBm` : null} />
            <InfoRow label="Last Association" value={device.wirelessLastSeenAt ? new Date(device.wirelessLastSeenAt).toLocaleString() : null} />
          </CollapsibleSection>
        )}

        <CollapsibleSection title="Discovery Info" icon={Eye}>
          <InfoRow label="First Seen" value={device.firstSeenAt ? new Date(device.firstSeenAt).toLocaleString() : null} />
          <InfoRow label="Last Seen" value={device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString() : null} />
          <InfoRow label="Discovery Methods" value={device.discoveryMethod?.join(', ')} />
          <InfoRow label="Firmware" value={device.firmware} />
          <InfoRow label="Serial Number" value={device.serialNumber} />
        </CollapsibleSection>

        {fp && (
          <CollapsibleSection title="HTTP Fingerprint" icon={Fingerprint}>
            <InfoRow label="Server" value={fp.serverHeader} mono />
            <InfoRow label="Powered By" value={fp.poweredBy} mono />
            <InfoRow label="Page Title" value={fp.title} />
            <InfoRow label="Favicon Hash" value={fp.faviconHash} mono />
            {fp.headers && Object.keys(fp.headers).length > 0 && (
              <div className="dd-sub-section">
                <div className="dd-sub-title">Response Headers</div>
                {Object.entries(fp.headers).map(([k, v]) => (
                  <InfoRow key={k} label={k} value={v} mono />
                ))}
              </div>
            )}
            {fp.banners && Object.keys(fp.banners).length > 0 && (
              <div className="dd-sub-section">
                <div className="dd-sub-title">Service Banners</div>
                {Object.entries(fp.banners).map(([port, banner]) => (
                  <InfoRow key={port} label={'Port ' + port} value={banner} mono />
                ))}
              </div>
            )}
          </CollapsibleSection>
        )}

        {(device.onvifXAddr || rtsp || device.onvifCapable) && (
          <CollapsibleSection title="Camera Details" icon={Camera}>
            <InfoRow label="ONVIF Address" value={device.onvifXAddr} mono />
            <InfoRow label="ONVIF Capable" value={device.onvifCapable ? 'Yes' : 'No'} />
            {device.onvifProfiles?.length > 0 && <InfoRow label="ONVIF Profiles" value={device.onvifProfiles.join(', ')} />}
            {rtsp && (
              <>
                <InfoRow label="RTSP Detected" value={rtsp.detected ? 'Yes' : 'No'} />
                <InfoRow label="RTSP URI" value={rtsp.uri} mono />
              </>
            )}
            <OnvifPromotion device={device} onUpdated={handleRefresh} />
          </CollapsibleSection>
        )}

        <CollapsibleSection title="Analytics" icon={Activity} defaultOpen={false}>
          <CameraAnalyticsSection key={device.id} cameraId={device.id} />
        </CollapsibleSection>

        {snmp && snmp.supported && (
          <CollapsibleSection title="SNMP Information" icon={Radio} defaultOpen={false}>
            <InfoRow label="System Name" value={snmp.sysName} />
            <InfoRow label="System Description" value={snmp.sysDescr} />
            <InfoRow label="Enterprise" value={snmp.enterprise} />
            <InfoRow label="Object ID" value={snmp.sysObjectID} mono />
            <InfoRow label="Contact" value={snmp.sysContact} />
            <InfoRow label="Location" value={snmp.sysLocation} />
            <InfoRow label="Uptime" value={snmp.sysUpTimeStr} />
            <InfoRow label="SNMP Version" value={snmp.version} />
            <InfoRow label="OS Info" value={snmp.osInfo} />
            {snmp.interfaces?.length > 0 && (
              <div className="dd-sub-section">
                <div className="dd-sub-title">Network Interfaces ({snmp.interfaceCount})</div>
                <div className="dd-table-mini">
                  <div className="dd-table-header"><span>Name</span><span>Type</span><span>Speed</span><span>Status</span></div>
                  {snmp.interfaces.map((iface, i) => (
                    <div key={i} className="dd-table-row">
                      <span className="dd-mono">{iface.description}</span>
                      <span>{iface.typeName}</span>
                      <span>{iface.speed > 0 ? (iface.speed / 1000000) + ' Mbps' : '-'}</span>
                      <span className={iface.operStatus === 1 ? 'dd-text-green' : 'dd-text-dim'}>{iface.operStatus === 1 ? 'Up' : 'Down'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CollapsibleSection>
        )}

        {bmc && bmc.supported && (
          <CollapsibleSection title="Server Hardware (BMC)" icon={Server}>
            <InfoRow label="BMC Type" value={bmc.bmcType} />
            <InfoRow label="BMC Model" value={bmc.bmcModel} />
            <InfoRow label="Firmware" value={bmc.firmwareVersion} />
            <InfoRow label="BIOS Version" value={bmc.biosVersion} />
            <InfoRow label="Host Name" value={bmc.hostName} />
            <InfoRow label="Manufacturer" value={bmc.manufacturer} />
            <InfoRow label="Model" value={bmc.model} />
            <InfoRow label="Serial Number" value={bmc.serialNumber} />
            <InfoRow label="Power State" value={bmc.powerState} />
            <InfoRow label="Power Consumption" value={bmc.powerConsumedWatts ? bmc.powerConsumedWatts + 'W' : null} />
            <InfoRow label="System Health" value={bmc.systemHealth} />
            <InfoRow label="Total Memory" value={bmc.totalMemoryGB ? bmc.totalMemoryGB + ' GB' : null} />
            {bmc.cpus?.length > 0 && (
              <div className="dd-sub-section">
                <div className="dd-sub-title">Processors</div>
                {bmc.cpus.map((cpu, i) => (
                  <div key={i} className="dd-hw-card">
                    <InfoRow label="Model" value={cpu.model} />
                    <InfoRow label="Cores / Threads" value={cpu.cores + 'C / ' + cpu.threads + 'T'} />
                    <InfoRow label="Speed" value={cpu.speedMHz ? cpu.speedMHz + ' MHz' : null} />
                  </div>
                ))}
              </div>
            )}
            {bmc.memoryDIMMs?.length > 0 && (
              <div className="dd-sub-section">
                <div className="dd-sub-title">Memory DIMMs</div>
                {bmc.memoryDIMMs.map((dimm, i) => (
                  <div key={i} className="dd-info-row">
                    <span className="dd-info-label">{dimm.slot}</span>
                    <span className="dd-info-value">{dimm.sizeGB}GB {dimm.type} @ {dimm.speedMHz}MHz</span>
                  </div>
                ))}
              </div>
            )}
            {bmc.storageControllers?.length > 0 && (
              <div className="dd-sub-section">
                <div className="dd-sub-title">Storage</div>
                {bmc.storageControllers.map((ctrl, i) => (
                  <div key={i} className="dd-hw-card">
                    <InfoRow label="Controller" value={ctrl.name + ' (' + ctrl.model + ')'} />
                    {ctrl.physicalDrives?.map((drv, j) => (
                      <div key={j} className="dd-drive-row">
                        <span className="dd-mono">{drv.name}</span>
                        <span>{drv.capacityGB?.toFixed(0)} GB {drv.mediaType}</span>
                        <span className="dd-text-green">{drv.status}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {bmc.temperatures?.length > 0 && (
              <div className="dd-sub-section">
                <div className="dd-sub-title">Temperatures</div>
                {bmc.temperatures.map((t, i) => (
                  <div key={i} className="dd-temp-row">
                    <span>{t.name}</span>
                    <span className={t.readingCelsius > (t.upperThresholdCritical || 80) ? 'dd-text-red' : 'dd-text-green'}>{t.readingCelsius}°C</span>
                    {t.upperThresholdCritical && <span className="dd-text-dim">/ {t.upperThresholdCritical}°C max</span>}
                  </div>
                ))}
              </div>
            )}
            {bmc.fans?.length > 0 && (
              <div className="dd-sub-section">
                <div className="dd-sub-title">Fans</div>
                {bmc.fans.map((f, i) => (
                  <div key={i} className="dd-temp-row">
                    <span>{f.name}</span>
                    <span>{f.rpm} RPM</span>
                    <span className="dd-text-green">{f.status}</span>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
        )}

        {device.metadata && Object.keys(device.metadata).length > 0 && (
          <CollapsibleSection title="Metadata" icon={Info} defaultOpen={false}>
            <pre className="dd-json-block">{JSON.stringify(device.metadata, null, 2)}</pre>
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
}
